<template>
  <div
    ref="loginViewport"
    :class="{ 'h-full overflow-y-auto overscroll-contain': isNative }"
    @focusin="revealFocusedField"
  >
    <div
      class="flex min-h-full flex-1 flex-col justify-center px-6 sm:px-6 lg:px-8"
      :class="isNative ? (isKeyboardOpen ? 'pb-6 pt-4' : 'pb-24 pt-12') : 'pt-24'"
    >
      <template v-if="isNative && !isKeyboardOpen">
        <div v-if="!isCheckingSso" class="fixed bottom-12 left-0 right-0 flex justify-center">
          <router-link
            to="/connect"
            class="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {{ t("msg.serverUrlChangeServer") }}
          </router-link>
        </div>

        <div v-if="bundleId" class="fixed bottom-4 left-0 right-0 flex justify-center">
          <span class="text-[10px] text-muted-foreground/50">Version {{ bundleId }}</span>
        </div>
      </template>

      <div
        class="no-focus-outline mx-auto w-full shrink-0"
        :class="isNative && isKeyboardOpen ? 'max-w-[10rem]' : 'max-w-sm sm:max-w-md'"
        ref="logoDiv"
        tabindex="-1"
      >
        <WordmarkLogo class="mx-auto" />
      </div>

      <div
        class="mx-auto flex w-full max-w-sm shrink-0 flex-col"
        :class="isNative && isKeyboardOpen ? 'mt-4 gap-3' : 'mt-6 gap-6'"
      >
        <RateLimitCountdown v-if="rateLimitSeconds > 0" :seconds="rateLimitSeconds" @expired="rateLimitSeconds = 0" />

        <p v-if="errorMessage && !isRateLimited" class="text-danger">{{ t("msg.error") }}: {{ errorMessage }}</p>

        <!-- Show loading state while checking SSO -->
        <div v-if="isCheckingSso" class="flex justify-center py-4"></div>

        <div
          v-else-if="noLoginMethod"
          data-testid="NoLoginMethod"
          class="rounded-md border border-input bg-muted/40 p-4 text-sm text-muted-foreground"
        >
          {{ t("msg.noLoginMethod") }}
        </div>

        <template v-else-if="samlOnlyMode">
          <!-- Web auto-fires a single IdP on mount and only renders buttons as a
             post-error retry. Native always renders them, and so does web when
             there are multiple IdPs to choose between: auto-firing would open a
             Custom Tab on every app launch / can't pick between IdPs. -->
          <template v-if="isNative || errorMessage || idps.length > 1">
            <button
              v-for="(idp, index) in idps"
              :key="idp.ID"
              :id="index === 0 ? 'login-sso-button' : undefined"
              :data-testid="`login-idp-${idp.ID || 'primary'}`"
              @click="samlRedirect(idp)"
              :class="
                twMerge(
                  'inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
                  `theme-${theme ?? 'grape'}${highAccessibility ? '-hc' : ''}`,
                )
              "
            >
              {{ samlLabel(idp) }}
            </button>
          </template>
        </template>

        <template v-else-if="useSaml">
          <button
            v-for="(idp, index) in idps"
            :key="idp.ID"
            :id="index === 0 ? 'login-sso-button' : undefined"
            :data-testid="`login-idp-${idp.ID || 'primary'}`"
            @click="samlRedirect(idp)"
            :class="
              twMerge(
                'inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
                `theme-${theme ?? 'grape'}${highAccessibility ? '-hc' : ''}`,
              )
            "
          >
            {{ samlLabel(idp) }}
          </button>

          <div class="relative">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
              <div class="w-full border-b border-foreground" />
            </div>
            <div class="relative flex justify-center text-sm font-medium leading-6">
              <span class="bg-background px-6">
                {{ t("msg.or") }}
              </span>
            </div>
          </div>

          <button
            type="button"
            @click="showForm = !showForm"
            :aria-expanded="showForm"
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0"
          >
            {{ $t("msg.localLogin") }}
            <ChevronDownIcon v-if="!showForm" class="ml-2 h-5 w-5" />
            <ChevronUpIcon v-if="showForm" class="ml-2 h-5 w-5" />
          </button>
        </template>

        <FormKit
          v-if="!isCheckingSso && !samlOnlyMode && !noLoginMethod && (!useSaml || showForm)"
          type="form"
          @submit="handleLogin"
          @submit-invalid="globals.focusFirstError"
          :actions="false"
          class="space-y-2"
        >
          <FormKit
            type="text"
            :classes="{ outer: { 'max-w-[20em]': false } }"
            :label="t('msg.username')"
            name="username"
            id="username"
            validation="required"
            placeholder="john.doe"
          />

          <FormKit
            type="password"
            :classes="{ outer: { 'max-w-[20em]': false } }"
            :label="t('msg.password')"
            name="password"
            id="password"
            validation="required"
            placeholder="••••••••••"
            autocomplete="off"
          />

          <button
            type="submit"
            :disabled="isRateLimited || isSubmitting"
            :class="globals.btnClass(theme, highAccessibility)"
            class="mt-6 flex w-full items-center justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            <font-awesome-icon
              v-if="isSubmitting"
              icon="fa-sharp-duotone fa-solid fa-spinner-third"
              spin
              class="mr-2"
            />
            {{ t("msg.login") }}
          </button>

          <div v-if="!isNative" class="text-right text-sm">
            <a href="/admin">Admin login is here</a>
          </div>
        </FormKit>

        <FormKit
          v-if="!isCheckingSso && !samlOnlyMode && !noLoginMethod && !isNative"
          v-model="kioskMode"
          label="This is a public computer"
          name="kioskMode"
          type="checkbox"
          :classes="{ outer: '!max-w-full flex justify-center' }"
        />

        <div
          v-if="isNative && biometricReady && !isCheckingSso && !samlOnlyMode && !noLoginMethod"
          class="mt-4 flex justify-center"
        >
          <button
            @click="handleBiometricLogin"
            :disabled="isSubmitting"
            class="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            <font-awesome-icon v-if="isSubmitting" icon="fa-sharp-duotone fa-solid fa-spinner-third" spin />
            <font-awesome-icon v-else icon="fa-solid fa-fingerprint" class="text-muted-foreground" />
            <span class="truncate">
              {{
                biometricUsername ? t("msg.biometricSignInAs", { name: biometricUsername }) : t("msg.biometricSignIn")
              }}
            </span>
          </button>
        </div>
      </div>

      <AppleBiometricSavePrompt
        v-if="isAppleNative"
        :show="showBiometricSavePrompt"
        :title="biometricUpdateFromUser ? t('msg.biometricUpdateTitle') : t('msg.biometricSaveTitle')"
        :description="
          biometricUpdateFromUser
            ? t('msg.biometricUpdateDescription', {
                oldUser: biometricUpdateFromUser,
                newUser: pendingBiometricCredentials?.username,
              })
            : t('msg.biometricSaveDescription')
        "
        :decline-label="t('msg.biometricSaveDecline')"
        :confirm-label="t('msg.biometricSaveConfirm')"
        :confirm-class="globals.btnClass(theme, highAccessibility)"
        :theme="theme"
        :high-contrast="highAccessibility"
        @decline="handleBiometricSaveDecline"
        @confirm="handleBiometricSaveConfirm"
      />

      <Dialog v-else :show="showBiometricSavePrompt">
        <DialogOverlay />

        <DialogPanel :theme="theme" :high-contrast="highAccessibility" class="!max-w-[400px]">
          <DialogTitle>
            {{ biometricUpdateFromUser ? t("msg.biometricUpdateTitle") : t("msg.biometricSaveTitle") }}
          </DialogTitle>

          <p class="text-sm text-muted-foreground">
            {{
              biometricUpdateFromUser
                ? t("msg.biometricUpdateDescription", {
                    oldUser: biometricUpdateFromUser,
                    newUser: pendingBiometricCredentials?.username,
                  })
                : t("msg.biometricSaveDescription")
            }}
          </p>

          <div class="flex gap-3">
            <button
              type="button"
              @click="handleBiometricSaveDecline"
              class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              {{ t("msg.biometricSaveDecline") }}
            </button>

            <button
              type="button"
              @click="handleBiometricSaveConfirm"
              :class="globals.btnClass(theme, highAccessibility)"
              class="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm"
            >
              {{ t("msg.biometricSaveConfirm") }}
            </button>
          </div>
        </DialogPanel>
      </Dialog>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { FormKit } from "@formkit/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";
import { useI18n } from "vue-i18n";
import { twMerge } from "tailwind-merge";

import globals from "../globals";
import api from "../services/api.ts";
import authService from "../services/auth.service.ts";

import WordmarkLogo from "../components/WordmarkLogo.vue";
import RateLimitCountdown from "../components/RateLimitCountdown.vue";
import AppleBiometricSavePrompt from "../components/AppleBiometricSavePrompt.vue";
import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../components/ui/dialog";
import { useAdminStore } from "../pinia/admin.ts";
import { useAuthStore } from "../pinia/auth.ts";
import { useThemeStore } from "../pinia/theme.ts";
import { REFERRER_QUERY_KEY, useRedirectUrl } from "../utils/useRedirectUrl";
import { resolveLoginIdps } from "../utils/loginIdpFallback";
import { setSamlAuthPending } from "../capacitor/auth-callback";
import { isAndroid, isNativePlatform } from "../capacitor/platform";
import { getServerUrl } from "../capacitor/server-url";
import {
  isBiometricAvailable,
  hasStoredCredentials,
  getStoredUsername,
  getCredentials,
  saveCredentials,
  deleteCredentials,
} from "../capacitor/biometric";
import { postLoginNavigate } from "../utils/postLoginNavigate";
import { completeBiometricSaveChoice } from "../utils/completeBiometricSaveChoice";

const { t } = useI18n();

const adminStore = useAdminStore();
const authStore = useAuthStore();
const themeStore = useThemeStore();
themeStore.fetchTheme();

const router = useRouter();
const route = useRoute();

const theme = computed(() => themeStore.accent);
const highAccessibility = computed(() => themeStore.highAccessibility);
const errorMessage = ref("");
const rateLimitSeconds = ref(0);
// Available user-login IdPs (primary + additional). An empty ID is the primary.
const idps = ref([]);
const useSaml = computed(() => idps.value.length > 0);
const disableLocalUserLogin = ref(false);
const isCheckingSso = ref(true);
const showForm = ref(false);
const kioskMode = ref(false);
const isRateLimited = computed(() => rateLimitSeconds.value > 0);
const isSubmitting = ref(false);
const isNative = isNativePlatform();
const isAppleNative = isNative && !isAndroid();
// Hides the local form and biometric (biometric posts to /api/login,
// which the server rejects when DisableLocalUserLogin is on). The
// "Change server" link sits outside this block so native can still
// switch backends.
const samlOnlyMode = computed(() => disableLocalUserLogin.value && useSaml.value);

// Local login is off and no IdP built, so nothing here can succeed. Without this
// every correct password 401s, which reads as a broken account.
const noLoginMethod = computed(() => disableLocalUserLogin.value && !useSaml.value);
const biometricReady = ref(false);
const biometricUsername = ref("");
const showBiometricSavePrompt = ref(false);
const pendingBiometricCredentials = ref(null);
const biometricUpdateFromUser = ref(null);
const bundleId = ref("");
const logoDiv = ref(null);
const loginViewport = ref(null);
const isKeyboardOpen = ref(false);

const { getReferrerUrl } = useRedirectUrl([]);

watch(showForm, (visible) => {
  if (visible) {
    setTimeout(() => {
      document.querySelector('input[name="username"]')?.focus();
    }, 50);
  }
});

// /api/system_status is already public, so unauthenticated visitors
// can read DisableLocalUserLogin without a dedicated endpoint.
// null means the request FAILED. An empty list means this deployment has no SAML,
// and rendering a failure as "no SAML" would hide the sign-in button.
const fetchLoginIdps = async () => {
  try {
    const list = await authService.getLoginIdps();
    return Array.isArray(list) ? list : [];
  } catch {
    return null;
  }
};

const checkSsoStatus = async () => {
  try {
    let [loginIdps, statusResp] = await Promise.all([fetchLoginIdps(), api.get("/system_status").catch(() => null)]);

    if (statusResp?.data?.DisableLocalUserLogin) {
      disableLocalUserLogin.value = true;
    }

    // One retry, because the usual cause is a burst against the shared cap and
    // the next moment is free.
    if (loginIdps === null) {
      await new Promise((resolve) => setTimeout(resolve, 750));
      loginIdps = await fetchLoginIdps();
    }

    idps.value = resolveLoginIdps(loginIdps, disableLocalUserLogin.value);
  } finally {
    isCheckingSso.value = false;
  }
};

// Each IdP uses its display name (the primary has an empty ID). An install that
// never named the primary keeps the generic single sign-on label.
const samlLabel = (idp) => idp?.DisplayName || t("msg.sso");

const samlRedirect = async (idp) => {
  const referrerUrl = getReferrerUrl();
  let url = "api/login/saml?kioskMode=" + kioskMode.value;

  if (idp && idp.ID) {
    url += "&idp=" + encodeURIComponent(idp.ID);
  }

  if (referrerUrl) {
    url += "&" + REFERRER_QUERY_KEY + "=" + encodeURIComponent(referrerUrl);
  }

  if (isNative) {
    const serverUrl = await getServerUrl();
    if (!serverUrl) return;

    // Check if the server supports mobile SAML (deep link callback flow).
    // Older servers redirect to /home in the browser instead.
    try {
      const status = await api.get("/system_status");
      if (!status.data?.MobileSaml) {
        errorMessage.value = t("msg.serverUpgradeRequiredForSso");
        return;
      }
    } catch {
      errorMessage.value = t("msg.error");
      return;
    }

    url = `${serverUrl}/${url}&mobile=true`;
    setSamlAuthPending();
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
    // The deep link handler in auth-callback.ts completes the flow
    return;
  }

  location.href = url;
};

function triggerButtonClickIfUnfocused() {
  const active = document.activeElement;
  const skipTags = ["INPUT", "TEXTAREA", "BUTTON", "SELECT"];

  const isInteractive = active && (skipTags.includes(active.tagName) || active.isContentEditable);

  if (!isInteractive) {
    const loginButton = document.getElementById("login-sso-button");
    if (loginButton) loginButton.click();
  }
}

function onKeyDown(e) {
  // Only auto-trigger on Enter with exactly one IdP. With several, Enter must not
  // silently pick whichever button renders first.
  if (e.key === "Enter" && idps.value.length === 1 && !isCheckingSso.value) {
    triggerButtonClickIfUnfocused();
  }
}

async function handleBiometricLogin() {
  if (!isNative) return;
  const serverUrl = await getServerUrl();
  if (!serverUrl) return;

  isSubmitting.value = true;
  errorMessage.value = "";

  let creds = null;
  try {
    creds = await getCredentials(serverUrl);
  } catch {
    isSubmitting.value = false;
    return;
  }

  if (!creds) {
    isSubmitting.value = false;
    return;
  }

  try {
    const credentials = {
      UserName: creds.username,
      Password: creds.password,
      KioskMode: kioskMode.value,
    };
    await authStore.login(JSON.stringify(credentials));
    await authStore.fetchStatus({ force: true });
    await postLoginNavigate(route, router);
  } catch (error) {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      await deleteCredentials(serverUrl);
      biometricReady.value = false;
      errorMessage.value = t("msg.biometricCredentialsExpired");
    } else {
      errorMessage.value = globals.renderError(t, error);
    }
  } finally {
    isSubmitting.value = false;
  }
}

async function handleLogin(fields, _) {
  if (rateLimitSeconds.value > 0) {
    return;
  }

  isSubmitting.value = true;

  const credentials = {
    UserName: fields.username,
    Password: fields.password,
    KioskMode: kioskMode.value,
  };

  try {
    await authStore.login(JSON.stringify(credentials));
    await authStore.fetchStatus({ force: true });

    // Offer to save or update credentials with biometrics on native
    if (isNative) {
      const available = await isBiometricAvailable();
      if (available) {
        const serverUrl = await getServerUrl();
        if (serverUrl) {
          const storedUser = await getStoredUsername(serverUrl);
          const isDifferentUser = storedUser && storedUser !== fields.username;
          const isNewSetup = !storedUser;

          if (isNewSetup || isDifferentUser) {
            pendingBiometricCredentials.value = {
              serverUrl,
              username: fields.username,
              password: fields.password,
            };
            biometricUpdateFromUser.value = isDifferentUser ? storedUser : null;
            showBiometricSavePrompt.value = true;
            return;
          }
        }
      }
    }

    await postLoginNavigate(route, router);
  } catch (error) {
    if (error.response) {
      if (error.response.status === 429) {
        const retryAfter = parseInt(error.response.headers["retry-after"] || "30", 10);
        rateLimitSeconds.value = retryAfter;
        errorMessage.value = "";
        return;
      }

      switch (error.response.data["Err"]) {
        case "err.totpVerificationRequired":
          router.push({ name: "TwoFactor", query: route.query });
          break;

        case "err.totpEnrolmentRequired":
          router.push({ name: "TwoFactorOnboard", query: route.query });
          break;

        case "err.unauthorised":
          if (isNative) {
            errorMessage.value = t("msg.adminLoginNotSupported");
            break;
          }
          await adminStore.fetchStatus({ force: true });
          if (adminStore.user.TotpEnabled) {
            if (adminStore.user.AdminTotpVerified) {
              window.location.href = "/admin/otp/verify";
            } else {
              window.location.href = "/admin/otp/onboard";
            }
          } else {
            window.location.href = "/admin/home";
          }
          break;
      }

      errorMessage.value = globals.renderError(t, error);
    }
  } finally {
    isSubmitting.value = false;
  }
}

async function handleBiometricSaveConfirm() {
  const creds = pendingBiometricCredentials.value;
  showBiometricSavePrompt.value = false;
  pendingBiometricCredentials.value = null;
  biometricUpdateFromUser.value = null;
  await completeBiometricSaveChoice(creds, saveCredentials, () => postLoginNavigate(route, router));
}

async function handleBiometricSaveDecline() {
  showBiometricSavePrompt.value = false;
  pendingBiometricCredentials.value = null;
  biometricUpdateFromUser.value = null;
  await completeBiometricSaveChoice(null, saveCredentials, () => postLoginNavigate(route, router));
}

async function checkServerAndLogin() {
  // Run auth check, SSO check, and biometric check in parallel
  // so the login form appears as fast as possible.
  const authCheck = authStore.fetchStatus({ force: true }).catch(() => null);
  const ssoCheck = checkSsoStatus();
  const serverUrl = isNative ? await getServerUrl() : null;
  const bioCheck =
    isNative && serverUrl
      ? Promise.all([isBiometricAvailable(), hasStoredCredentials(serverUrl), getStoredUsername(serverUrl)])
      : Promise.resolve(null);

  const [user, , bioResult] = await Promise.all([authCheck, ssoCheck, bioCheck]);

  if (user) {
    await postLoginNavigate(route, router);
    return;
  }

  if (bioResult) {
    const [available, stored, username] = bioResult;
    biometricReady.value = available && stored;
    biometricUsername.value = username ?? "";
  }

  // Native waits for an explicit tap, or a Custom Tab opens on every launch. The error
  // guard stops a failing IdP looping, and several IdPs mean the user must choose.
  if (samlOnlyMode.value && idps.value.length === 1 && !isNative && !route.query.error) {
    if (route.query.sessionExpired) {
      router.replace({ name: "LoggedOut" });
      return;
    }
    samlRedirect(idps.value[0]);
  }
}

let viewportInitialHeight = 0;

function handleViewportResize() {
  if (window.visualViewport) {
    isKeyboardOpen.value = window.visualViewport.height < viewportInitialHeight * 0.75;
  }
}

// On native, @capacitor/keyboard fires precise lifecycle events. Returns a
// disposer that the unmount path calls. The viewport-resize heuristic is
// the web fallback only. It misfires on iPad split-view where the keyboard
// takes well under 25% of the screen height.
let keyboardListenerDispose = null;
let loginUnmounted = false;
let focusFrame = null;

async function revealFocusedField() {
  if (!isNative || !isKeyboardOpen.value) return;
  await nextTick();
  if (loginUnmounted) return;
  if (focusFrame !== null) cancelAnimationFrame(focusFrame);
  focusFrame = requestAnimationFrame(() => {
    focusFrame = null;
    const input = document.activeElement;
    if (input?.matches("input, textarea") && loginViewport.value?.contains(input)) {
      // Include the label and validation message, not just the input itself.
      (input.closest(".formkit-outer") ?? input).scrollIntoView({ block: "nearest", behavior: "instant" });
    }
  });
}

async function installNativeKeyboardListeners() {
  const { Keyboard } = await import("@capacitor/keyboard");
  if (loginUnmounted) return;
  const handles = [];
  const listen = async (event, callback) => {
    const handle = await Keyboard.addListener(event, callback);
    if (loginUnmounted) await handle.remove();
    else handles.push(handle);
  };
  keyboardListenerDispose = () => handles.forEach((handle) => handle.remove());
  await Promise.all([
    listen("keyboardWillShow", () => {
      isKeyboardOpen.value = true;
    }),
    // Body resize finishes with the keyboard animation. Scrolling on willShow
    // would use the old height and can leave the password under the keyboard.
    listen("keyboardDidShow", revealFocusedField),
    listen("keyboardWillHide", () => {
      isKeyboardOpen.value = false;
    }),
  ]);
}

// React to route.query.error changes, not just the initial mount. SAML
// errors on native arrive via the knocknoc://auth-callback deep link, which
// pushes to the Login route with ?error=<key>. When the user is already on
// Login (the common case), router.push does not remount the component, so
// onMounted's one-shot check would miss the error.
watch(
  () => route.query.error,
  (key) => {
    if (typeof key === "string" && key) {
      errorMessage.value = t(key);
    }
  },
  { immediate: true },
);

onMounted(async () => {
  logoDiv.value?.focus();

  if (route.query.error) {
    errorMessage.value = t(route.query.error);
  }

  window.addEventListener("keydown", onKeyDown);

  if (isNative) {
    installNativeKeyboardListeners();
  } else if (window.visualViewport) {
    viewportInitialHeight = window.visualViewport.height;
    window.visualViewport.addEventListener("resize", handleViewportResize);
  }

  if (isNative) {
    try {
      const { default: AppUpdater } = await import("../capacitor/app-updater-plugin");
      const current = await AppUpdater.current();
      if (current.id && current.id !== "builtin") bundleId.value = current.id;
    } catch {
      // Still show the installed version if the updater is unavailable.
    }
    if (!bundleId.value) {
      try {
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        bundleId.value = info.build ? `${info.version} (${info.build})` : info.version;
      } catch {
        // A version lookup must not prevent signing in.
      }
    }
  }

  // On native, the router guard guarantees a server URL exists by the time we get here
  await checkServerAndLogin();
});

onBeforeUnmount(() => {
  loginUnmounted = true;
  if (focusFrame !== null) cancelAnimationFrame(focusFrame);
  window.removeEventListener("keydown", onKeyDown);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", handleViewportResize);
  }
  keyboardListenerDispose?.();
  keyboardListenerDispose = null;
});
</script>

<style scoped>
.no-focus-outline:focus {
  outline: none;
}

.theme-teal {
  @apply bg-theme-teal hover:bg-theme-teal/90 focus-visible:outline-theme-teal;
}

.theme-teal-hc {
  @apply bg-theme-teal-hc hover:bg-theme-teal-hc/90 focus-visible:outline-theme-teal-hc;
}

.theme-grape {
  @apply bg-theme-grape hover:bg-theme-grape/90 focus-visible:outline-theme-grape;
}

.theme-grape-hc {
  @apply bg-theme-grape-hc hover:bg-theme-grape-hc/90 focus-visible:outline-theme-grape-hc;
}

.theme-iceberg {
  @apply bg-theme-iceberg hover:bg-theme-iceberg/90 focus-visible:outline-theme-iceberg;
}

.theme-iceberg-hc {
  @apply bg-theme-iceberg-hc hover:bg-theme-iceberg-hc/90 focus-visible:outline-theme-iceberg-hc;
}

.theme-rose {
  @apply bg-theme-rose hover:bg-theme-rose/90 focus-visible:outline-theme-rose;
}

.theme-rose-hc {
  @apply bg-theme-rose-hc hover:bg-theme-rose-hc/90 focus-visible:outline-theme-rose-hc;
}

.theme-offsite {
  @apply bg-theme-offsite hover:bg-theme-offsite/90 focus-visible:outline-theme-offsite;
}

.theme-offsite-hc {
  @apply bg-theme-offsite-hc hover:bg-theme-offsite-hc/90 focus-visible:outline-theme-offsite-hc;
}
</style>
